<script setup lang="ts">
  import { ICreateOptionDto } from '@/api/services/options.service';
  import V2ModalOption from '@/components/modals/option/V2ModalOption.vue';
  import { modalOptionSettings } from '@/components/modals/option/modal-option-settings';
  import OptionsSelector from '@/components/modals/OptionsSelector.vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiPopover from '@/components/pos-v2/ui/PosUiPopover.vue';
  import { useModal } from '@/composition/use-modal';
  import { IBranch } from '@/interfaces/IBranch';
  import { IItem } from '@/interfaces/IItem';
  import { IOption } from '@/interfaces/IOption';
  import { IOptionItem } from '@/interfaces/IOptionItem';
  import { IRestaurant } from '@/interfaces/IRestaurant';
  import { computed, set } from 'vue';
  import V2ItemOptionRow, { IItemOptionRow } from './V2ItemOptionRow.vue';

  const props = defineProps<{
    /** Owned by NewItemModal; this section mutates rows in place. */
    itemOptions: IItemOptionRow[];
    item: IItem;
    restaurant: IRestaurant;
    branch: IBranch;
    menuId: number | null;
  }>();

  const modals = useModal();

  const visibleRows = computed(() =>
    props.itemOptions.filter((row) => !row.deleted)
  );

  // Stable keys for draft rows without polluting the API payload:
  // keyed on row object identity, which survives option replacement on save.
  let nextLocalKey = -1;
  const localKeys = new WeakMap<IItemOptionRow, number>();
  function rowKey(row: IItemOptionRow): number {
    if (row.option.id) return row.option.id;
    let key = localKeys.get(row);
    if (!key) {
      key = nextLocalKey--;
      localKeys.set(row, key);
    }
    return key;
  }

  const MODAL_OPTION_SETTINGS = modalOptionSettings();

  //#region Add / link
  function createOption() {
    modals.show(
      V2ModalOption,
      {
        deferred: true,
      },
      MODAL_OPTION_SETTINGS,
      {
        draftSaved: (draft: ICreateOptionDto) => {
          props.itemOptions.push({
            option: draft as IOption,
            active: true,
            isDraft: true,
          });
          reindexRows();
        },
      }
    );
  }

  function lookupOptions() {
    modals.show(
      OptionsSelector,
      {
        restaurant: props.restaurant,
        branch: props.branch,
        menuId: props.menuId,
        item: props.item,
      },
      {
        adaptive: true,
        height: 'auto',
        scrollable: true,
        width: 900,
        maxWidth: window.innerWidth * 0.95,
      },
      {
        use: (option: IOption) => {
          props.itemOptions.push({
            option,
            active: true,
          });
          reindexRows();
        },
        clone: (option: IOption) => {
          // The clone is unsaved content: it must flow as a draft so the item
          // save creates it via POST /options (links can't carry content).
          const cloned: IOption = JSON.parse(JSON.stringify(option));
          delete cloned.id;
          cloned.itemId = null;
          cloned.optionItems = (cloned.optionItems ?? []).map(
            (oI: IOptionItem) => {
              const { id, optionId, ...rest } = oI;
              return rest as IOptionItem;
            }
          );
          props.itemOptions.push({
            option: cloned,
            active: true,
            isDraft: true,
          });
          reindexRows();
        },
      }
    );
  }
  //#endregion

  //#region Edit
  function editRow(row: IItemOptionRow) {
    if (row.isDraft) {
      modals.show(
        V2ModalOption,
        {
          deferred: true,
          draftData: row.option,
        },
        MODAL_OPTION_SETTINGS,
        {
          draftSaved: (draft: ICreateOptionDto) => {
            set(row, 'option', draft as IOption);
          },
        }
      );
      return;
    }

    modals.show(
      V2ModalOption,
      {
        optionToEdit: row.option,
        duplicateAsDraft: true,
      },
      MODAL_OPTION_SETTINGS,
      {
        saved: (savedOption: IOption) => {
          // Refresh the local snapshot so the item save can't use stale data.
          set(row, 'option', savedOption);
        },
        draftSaved: (draft: ICreateOptionDto) => {
          // The user duplicated a shared option: tombstone the old link and
          // add the independent copy as a draft.
          removeRow(row);
          props.itemOptions.push({
            option: draft as IOption,
            active: true,
            isDraft: true,
          });
          reindexRows();
        },
      }
    );
  }
  //#endregion

  //#region Row operations
  function reindexRows() {
    props.itemOptions.forEach((row, idx) => {
      set(row, 'index', idx);
    });
  }

  function removeRow(row: IItemOptionRow) {
    if (!row.option.id) {
      props.itemOptions.splice(props.itemOptions.indexOf(row), 1);
    } else {
      set(row, 'deleted', true);
    }
    reindexRows();
  }

  function moveRow(row: IItemOptionRow, direction: -1 | 1) {
    const visible = visibleRows.value;
    const visibleIdx = visible.indexOf(row);
    const target = visible[visibleIdx + direction];
    if (!target) return;

    const fromIdx = props.itemOptions.indexOf(row);
    const toIdx = props.itemOptions.indexOf(target);
    props.itemOptions.splice(fromIdx, 1);
    props.itemOptions.splice(toIdx, 0, row);
    reindexRows();
  }
  //#endregion
</script>

<template>
  <div class="item-options-section">
    <p class="section-intro">{{ $t('itemOptions.intro') }}</p>

    <div class="section-toolbar">
      <span v-show="visibleRows.length === 0" class="empty-hint">
        {{ $t('itemOptions.empty') }}
      </span>
      <div class="toolbar-actions">
        <PosUiPopover placement="bottom" align="end">
          <template #trigger="{ toggle }">
            <PosUiButton
              color="neutral"
              variant="outline"
              size="sm"
              icon="plus"
              @click="toggle"
            >
              {{ $t('itemOptions.addOption') }}
            </PosUiButton>
          </template>
          <template #default="{ close }">
            <div class="popover-menu">
              <button
                class="popover-menu-item"
                @click="
                  createOption();
                  close();
                "
              >
                {{ $t('itemOptions.newOption') }}
              </button>
              <button
                class="popover-menu-item"
                @click="
                  lookupOptions();
                  close();
                "
              >
                {{ $t('itemOptions.existingOption') }}
              </button>
            </div>
          </template>
        </PosUiPopover>
      </div>
    </div>

    <div v-show="visibleRows.length > 0" class="rows-list">
      <transition-group name="option-rows" tag="div" class="rows-list-inner">
        <V2ItemOptionRow
          v-for="row in props.itemOptions"
          v-show="!row.deleted"
          :key="rowKey(row)"
          :item-option="row"
          :first="visibleRows[0] === row"
          :last="visibleRows[visibleRows.length - 1] === row"
          @moveUp="moveRow(row, -1)"
          @moveDown="moveRow(row, 1)"
          @edit="editRow(row)"
          @remove="removeRow(row)"
        />
      </transition-group>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .item-options-section {
    @apply tw-flex tw-flex-col tw-gap-3 tw-pt-3;

    .section-intro {
      @apply tw-mb-0 tw-text-sm tw-text-neutral-500;
    }

    .section-toolbar {
      @apply tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2;

      .empty-hint {
        @apply tw-text-sm tw-text-neutral-500;
      }

      .toolbar-actions {
        @apply tw-ml-auto tw-flex tw-items-center tw-gap-2;
      }
    }

    .popover-menu {
      @apply tw-flex tw-flex-col;

      .popover-menu-item {
        @apply tw-rounded-lg tw-px-3 tw-py-2.5 tw-text-left tw-text-sm tw-text-neutral-700 tw-transition-colors;

        &:hover {
          @apply tw-bg-neutral-100;
        }

        &:focus-visible {
          @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
        }
      }
    }

    .rows-list {
      .rows-list-inner {
        @apply tw-flex tw-flex-col tw-gap-2;
      }
    }
  }
</style>
